import appsApi from '@/api/apps.js'
import urlsApi from '@/api/urlSetting.js'
import childApi from '@/api/child.js'
import deviceApi from '@/api/device.js'
import youtubeApi from '@/api/youtube.js'
import contentFiltersApi from '@/api/contentFilters.js'
import { addVideoTitle } from '@/services/youtube'

export const resetState = ({ commit }) => {
  commit('resetState')
}

/**
 * Load all children in group.
 *
 * @param commit
 * @param dispatch
 * @param groupId
 * @returns Promise
 */
export const load = async ({ commit }) => {
  const children = await childApi.get([
    "base_info",
    "latest_accumulated_screentime",
    "latest_location",
    "avatar_download_url",
    "parental_modes",
    "modes",
    "time_blocks",
    "current_rule",
    "child_status",
    "named_location_count",
    "request_count"
  ])

  //console.log(children, 'CHILDREN')

  commit('setChildren', children)
}

/**
 * Load child status.
 *
 * @param commit
 * @param dispatch
 * @param groupId
 * @returns Promise
 */
export const loadStatus = async ({ commit }, child_id) => {
  const child = await childApi.get(
    [
    "latest_accumulated_screentime",
    "current_rule",
    "child_status"
    ],
    [child_id]
  )

  const {
    latest_accumulated_screentime,
    primary_device_id,
    current_rule,
    status
  } = child[0]

  commit('updateChild', {
    child_id,
    attributes: {
      status,
      current_rule,
      primary_device_id,
      latest_accumulated_screentime
    }
  })
}

/**
 * Add new child.
 *
 * @param child
 */
export const addChild = ({ commit }, child) => {
  commit('addChild', child)
}

/**
 * Add new child device.
 *
 * @param child_id
 * @param device
 */
export const addChildDevice = async ({ commit, dispatch }, { child_id, device }) => {
  await dispatch('updateChildrenStatuses')
  await dispatch('loadStatus', child_id)

  commit('addChildDevice', {
    child_id,
    device
  })
}

export const updateChildDevice = ({ commit }, { child_id, device_id, data }) => {
  commit('updateChildDevice', {
    child_id,
    device_id,
    attributes: data
  })
}

/**
 * Override child screen time for selected day.
 *
 * @param commit
 * @param child_id
 * @param date
 * @param limit
 * @returns Promise
 */
export const setScreenTimeLimit = ({ commit }, { child_id, date, limit }) => {
  return childApi.setScreenTimeLimit(child_id, { date, limit }).then(() => {
    commit('updateChild', {
      id: child_id,
      attributes: {
        screen_time_override_in_minutes: limit,
        screen_time_override_date: date
      }
    })
  })
}

/**
 * Update child.
 *
 * @param commit
 * @param child_id
 * @param data
 * @returns Promise
 */
export const updateChild = ({ commit }, { child_id,  data }) => {
  return childApi.update(child_id, data).then(() => {
    commit('updateChild', {
      child_id: child_id,
      attributes: data
    })
  })
}

/**
 * Update child location.
 *
 * @param commit
 * @param child_id
 * @param data
 * @returns Promise
 */
export const updateChildLocation = async ({ commit }, child_id) => {
  const child = await childApi.get(
    [
      'latest_location',
      'child_status'
  ] ,
    [child_id]
  )

  const { latest_location, status } = child[0]

  commit('updateChild', {
    child_id,
    attributes: {
      latest_location,
      status
    }
  })
}

/**
 * Update child from publish, without API calls.
 *
 * @param commit
 * @param child_id
 * @param data
 */
export const updateChildFromPublish = ({ commit }, { child_id,  data }) => {
  commit('updateChild', {
    child_id: child_id,
    attributes: data
  })
}

export const updateChildStatus = ({ commit }, { child_id,  active }) => {
  commit('updateChildStatus', {
    child_id: child_id,
    status: active
  })
}

/**
 * Update child settings.
 *
 * @param commit
 * @param child_id
 * @param data
 * @returns Promise
 */
export const updateChildSettings = ({ commit }, { child_id,  data }) => {
  return childApi.updateSettings(child_id, data).then(() => {
    commit('updateChildSettings', {
      child_id: child_id,
      attributes: data
    })
  })
}

/**
 * Update child settings form publish, without API calls.
 *
 * @param commit
 * @param child_id
 * @param data
 */
export const updateChildSettingsFromPublish = ({ commit }, { child_id,  data }) => {
  commit('updateChildSettings', {
    child_id: child_id,
    attributes: data
  })
}

/**
 * Change child primary device.
 *
 * @param commit
 * @param childId
 * @param deviceId
 * @returns Promise
 */
export const changePrimaryDevice = ({ commit }, { childId, deviceId }) => {
  return deviceApi.setPrimary(childId, deviceId).then(() => {
    commit('changePrimaryDevice', {
      childId,
      deviceId
    })
  })
}

/**
 * Update child device last seen.
 *
 * @param device_id
 * @param last_seen
 *
 * @returns Promise
 */
export const updateLastSeen = ({ commit }, { device_id, last_seen }) => {
  commit('setLastSeen', {
    device_id,
    last_seen
  })
}

export const updateChildrenStatuses = async ({ commit }) => {
  const children = await childApi.get([
    "latest_accumulated_screentime",
    "current_rule",
    "child_status"
  ])

  children.forEach(child => {
    commit('updateChild', {
      child_id: child.id,
      attributes: {
        status: child.status,
        current_rule: child.current_rule,
        latest_accumulated_screentime: child.latest_accumulated_screentime
      }
    })
  })
}

/**
 * Remove child device.
 *
 * @param commit
 * @param deviceId
 * @returns Promise
 */
export const removeChildDevice = async ({ commit, dispatch }, deviceId ) => {
  await deviceApi.remove(deviceId)
  await dispatch('updateChildrenStatuses')

  commit('removeChildDevice', deviceId)
}

/**
 * Remove child device publish event.
 *
 * @param commit
 * @param deviceId
 */
export const removeChildDeviceFromPublish = ({ commit }, deviceId ) => {
  commit('removeChildDevice', deviceId)
}

/**
 * Remove child.
 *
 * @param commit
 * @param childId
 * @returns Promise
 */
export const removeChild = ({ commit }, childId) => {
  return childApi.delete(childId).then(() => {
    commit('removeChild', childId)
  })
}

/**
 * Remove child publish event.
 *
 * @param commit
 * @param childId
 */
export const removeChildFromPublish = ({ commit }, childId) => {
  commit('removeChild', childId)
}

export const setSearches = async ({ commit }, { user_id, event_names }) => {
  const searches = await childApi.getDeviceEvents(user_id, event_names)

  // 'engine' = 4 means YouTube search,
  // for YouTube searches there is a separate tab
  const filteredSearches = searches.filter(item => item.details.engine !== '4')

  commit('setSearches', {
    user_id,
    searches: filteredSearches
  })
}

export const setYoutubeSearches = async ({ commit }, { user_id, event_names }) => {
  const youtube_searches = await childApi.getDeviceEvents(user_id, event_names)

  // find YouTube seraches for YouTube tab ('engine' === 4)
  const filteredSearches = youtube_searches.filter(item => item.details.engine === '4')

  commit('setYoutubeSearches', {
    user_id,
    youtube_searches: filteredSearches
  })
}

export const setYoutubeViews = async ({ commit }, { user_id, min_date, max_date }) => {
  const youtube_views = await youtubeApi.getViews(user_id, min_date, max_date)

  commit('setYoutubeViews', {
    user_id,
    youtube_views
  })
}

export const setBlocksAndAlerts = async ({ commit }, { user_id, event_names }) => {
  const blocks_and_alerts = await childApi.getDeviceEvents(user_id, event_names)

  commit('setBlocksAndAlerts', {
    user_id,
    blocks_and_alerts
  })
}

export const addSearchEvent = ({ commit }, { user_id, device_event }) => {
  commit('addSearchEvent', {
    user_id,
    device_event
  })
}

export const addYoutubeSearchEvent = ({ commit }, { user_id, device_event }) => {
  commit('addYoutubeSearchEvent', {
    user_id,
    device_event
  })
}

export const addYoutubeViewEvent = ({ commit }, { user_id, youtube_event }) => {
  commit('addYoutubeViewEvent', {
    user_id,
    youtube_event: addVideoTitle(youtube_event)
  })
}

export const addBlocksAndAlertsEvent = ({ commit }, { user_id, device_event }) => {
  commit('addBlocksAndAlertsEvent', {
    user_id,
    device_event
  })
}

export const setCategorySettings = async ({ commit }, { user_id, mode_id }) => {
  const category_settings = await contentFiltersApi.getByModeId(mode_id)

  commit('setCategorySettings', {
    user_id,
    category_settings
  })
}

export const updateCategorySettings = async ({ commit }, { user_id, mode_id, category_id, action }) => {
  const category_settings = await contentFiltersApi.setCategory(
    mode_id,
    category_id,
    action
  )

  commit('updateCategorySettings', {
    user_id,
    category_settings: category_settings[0]
  })
}

export const removeCategorySettings = async ({ commit }, { user_id, mode_id, category_id }) => {
  await contentFiltersApi.delete(
    mode_id,
    category_id
  )

  commit('removeCategorySettings', {
    user_id,
    category_id
  })
}

export const loadBlockableApps = async ({ commit }, child_id) => {
  const android_apps = await appsApi.getBlockableApps(child_id, 'android')
  const ios_apps = await appsApi.getBlockableApps(child_id, 'ios')

  commit('setBlockableApps', {
    child_id,
    android_apps,
    ios_apps
  })
}

export const loadAppSettings = async ({ commit }, { user_id, mode_id }) => {
  const app_settings = await appsApi.getAppSettings(mode_id)

  commit('setAppSettings', {
    user_id,
    app_settings
  })
}

export const loadSocialMedia = async ({ commit }, user_id) => {
  const {
    protected_social_media_settings,
    unsupported_social_media_settings
  } = await appsApi.getSocialMediaSettings(user_id)

  commit('setProtectedSocialMedia', {
    user_id,
    protected_social_media_settings
  })

  commit('setUnsupportedSocialMedia', {
    user_id,
    unsupported_social_media_settings
  })
}

export const blockAllSocialMedia = async ({ commit }, user_id) => {
  const {
    protected_social_media_settings,
    unsupported_social_media_settings
  } = await appsApi.blockAllSocialMedia(user_id)

  commit('setProtectedSocialMedia', {
    user_id,
    protected_social_media_settings
  })

  commit('setUnsupportedSocialMedia', {
    user_id,
    unsupported_social_media_settings
  })
}

export const allowAllSocialMedia = async ({ commit }, user_id) => {
  const {
    protected_social_media_settings,
    unsupported_social_media_settings
  } = await appsApi.allowAllSocialMedia(user_id)

  commit('setProtectedSocialMedia', {
    user_id,
    protected_social_media_settings
  })

  commit('setUnsupportedSocialMedia', {
    user_id,
    unsupported_social_media_settings
  })
}

export const blockUnsupportedSocialMedia = async ({ commit }, user_id) => {
  const {
    unsupported_social_media_settings
  } = await appsApi.blockUnsupportedSocialMedia(user_id)

  commit('setUnsupportedSocialMedia', {
    user_id,
    unsupported_social_media_settings
  })
}

export const allowUnsupportedSocialMedia = async ({ commit }, user_id) => {
  const {
    unsupported_social_media_settings
  } = await appsApi.allowUnsupportedSocialMedia(user_id)

  commit('setUnsupportedSocialMedia', {
    user_id,
    unsupported_social_media_settings
  })
}

export const updateProtectedSocialMedia = async ({ commit }, { user_id, app_name, app_action }) => {
  const social_media_setting = await appsApi.setSocialMediaSettings(user_id, app_name, app_action)

  commit('updateProtectedSocialMedia', {
    user_id,
    social_media_setting
  })
}

export const updateUnsupportedSocialMedia = async ({ commit }, { user_id, app_name, app_action }) => {
  const social_media_setting = await appsApi.setSocialMediaSettings(user_id, app_name, app_action)

  commit('updateUnsupportedSocialMedia', {
    user_id,
    social_media_setting
  })
}

export const updateAppSettings = async ({ commit }, { user_id, mode_id, app_id, action }) => {
  const app_settings = await appsApi.setAppSettings(
    mode_id,
    app_id,
    action
  )

  commit('updateAppSettings', {
    user_id,
    app_settings
  })
}

export const setUrlSettings = async ({ commit }, { user_id, mode_id }) => {
  const url_settings = await urlsApi.getByModeId(mode_id)

  commit('setUrlSettings', {
    user_id,
    url_settings
  })
}

export const addUrlSettings = async ({ commit }, { user_id, url_settings }) => {
  commit('addUrlSettings', {
    user_id,
    url_settings
  })
}

export const removeUrlSettings = async ({ commit }, { user_id, url_settings }) => {
  commit('removeUrlSettings', {
    user_id,
    url_settings
  })
}